import { z } from "zod";
import { mediaListSchema } from "../common.js";

export const VT_CONNECTION_KINDS = ["electrical", "water", "wastewater"] as const;
export type VtConnectionKind = (typeof VT_CONNECTION_KINDS)[number];

export const VT_CONNECTION_KIND_LABELS: Record<VtConnectionKind, string> = {
  electrical: "Électricité",
  water: "Eau potable",
  wastewater: "Eaux usées",
};

export const VT_ELEC_CONNECTION_TYPES = ["existing", "new"] as const;

/**
 * Quand l'installation existante est en triphasé, deux solutions s'offrent au
 * client — et elles ne coûtent pas la même chose :
 *
 * - `tableau_triphase` : on pose un tableau triphasé pré-câblé (option du
 *   catalogue PVM), et le câble est dimensionné en section TRIPHASÉE ;
 * - `connexion_monophasee` : on se raccorde en monophasé, section MONOPHASÉE,
 *   et c'est l'électricien du client qui équilibrera les phases.
 *
 * Avant le 22/10/2026 le choix n'existait pas : toute installation triphasée
 * était calculée en triphasé et se voyait proposer le tableau. L'absence de
 * valeur vaut donc `tableau_triphase`, pour que les VT déjà saisies gardent
 * exactement leur résultat.
 */
export const VT_ELEC_TRIPHASE_SOLUTIONS = ["tableau_triphase", "connexion_monophasee"] as const;
export type VtElecTriphaseSolution = (typeof VT_ELEC_TRIPHASE_SOLUTIONS)[number];

export const VT_ELEC_TRIPHASE_SOLUTION_LABELS: Record<VtElecTriphaseSolution, string> = {
  tableau_triphase: "Tableau triphasé",
  connexion_monophasee: "Connexion monophasée",
};
export const VT_ELEC_CABLE_SECTIONS = [
  "6_mono",
  "10_mono",
  "16_mono",
  "25_mono",
  "35_mono",
  "4_tri",
  "6_tri",
  "10_tri",
  "16_tri",
  "25_tri",
  "35_tri",
] as const;
export type VtElecCableSection = (typeof VT_ELEC_CABLE_SECTIONS)[number];

export const VT_DISJONCTEUR_KINDS = ["differentiel", "normal"] as const;
export const VT_OUI_NON = ["oui", "non"] as const;

export const VT_WATER_CONNECTION_TYPES = ["existing", "to_create"] as const;
export const VT_PIPE_DIAMETERS = ["20mm", "25mm", "32mm", "40mm", "autre"] as const;
export const VT_PIPE_MATERIALS = ["pvc", "per", "copper", "pe", "acier"] as const;

export const VT_WASTEWATER_DIAMETERS = ["100mm", "125mm", "160mm", "200mm", "autre"] as const;
export const VT_WASTEWATER_MATERIALS = ["pvc", "gres", "beton", "autre"] as const;

// Libellés FR (source unique : formulaires + PDF). L'international relèvera de
// la génération PDF côté admin, pas du formulaire local.
export const VT_ELEC_CONNECTION_TYPE_LABELS: Record<
  (typeof VT_ELEC_CONNECTION_TYPES)[number],
  string
> = {
  existing: "Sur installation existante",
  new: "Nouveau branchement",
};

export const VT_ELEC_CABLE_SECTION_LABELS: Record<VtElecCableSection, string> = {
  "4_tri": "4 mm² tri",
  "6_mono": "6 mm² mono",
  "10_mono": "10 mm² mono",
  "16_mono": "16 mm² mono",
  "25_mono": "25 mm² mono",
  "35_mono": "35 mm² mono",
  "6_tri": "6 mm² tri",
  "10_tri": "10 mm² tri",
  "16_tri": "16 mm² tri",
  "25_tri": "25 mm² tri",
  "35_tri": "35 mm² tri",
};

export const VT_WATER_CONNECTION_TYPE_LABELS: Record<
  (typeof VT_WATER_CONNECTION_TYPES)[number],
  string
> = {
  existing: "Sur réseau existant",
  to_create: "À créer (viabilisation)",
};

export const VT_PIPE_MATERIAL_LABELS: Record<
  (typeof VT_PIPE_MATERIALS)[number],
  string
> = {
  pvc: "PVC",
  per: "PER",
  copper: "Cuivre",
  pe: "PE",
  acier: "ACIER",
};

export const VT_WASTEWATER_MATERIAL_LABELS: Record<
  (typeof VT_WASTEWATER_MATERIALS)[number],
  string
> = {
  pvc: "PVC",
  gres: "Grès",
  beton: "Béton",
  autre: "Autre",
};

export const vtUtilitiesSchema = z
  .object({
    hasConnections: z.boolean().optional(),
    requiredConnections: z.array(z.enum(VT_CONNECTION_KINDS)).optional(),

    // Electrical
    elecConnectionType: z.enum(VT_ELEC_CONNECTION_TYPES).optional(),
    elecAerien: z.boolean().optional(),
    elecAerienLength: z.number().optional(),
    elecAerienGaine: z.boolean().optional(),
    elecAerienGaineLength: z.number().optional(),
    elecEnterre: z.boolean().optional(),
    elecEnterreLength: z.number().optional(),
    elecInterieur: z.boolean().optional(),
    elecInterieurLength: z.number().optional(),
    elecCarrossable: z.boolean().optional(),
    elecCarrossableLength: z.number().optional(),
    extraLengthToPanel: z.number().optional(),
    elecConsuel: z.enum(VT_OUI_NON).optional(),
    ligneDeTerre: z.boolean().optional(),
    cableSection: z.enum(VT_ELEC_CABLE_SECTIONS).optional(),
    forcedCableSection: z.enum(VT_ELEC_CABLE_SECTIONS).optional(),
    forcedSectionRemark: z.string().optional(),
    elecEmplacementLibre: z.enum(VT_OUI_NON).optional(),
    elecInstallationTriphase: z.enum(VT_OUI_NON).optional(),
    elecTriphaseSolution: z.enum(VT_ELEC_TRIPHASE_SOLUTIONS).optional(),
    elecDisjoncteur: z.enum(VT_DISJONCTEUR_KINDS).optional(),
    electricalNotes: z.string().optional(),
    elecKitEtancheite: z.enum(VT_OUI_NON).optional(),
    photoCheminementElec: mediaListSchema.optional(),

    // Water
    waterConnectionType: z.enum(VT_WATER_CONNECTION_TYPES).optional(),
    waterConnectExistingHotWater: z.enum(VT_OUI_NON).optional(),
    photoRaccordementEauChaude: mediaListSchema.optional(),
    waterPipeDiameter: z.enum(VT_PIPE_DIAMETERS).optional(),
    waterPipeDiameterAutreDetail: z.string().optional(),
    waterPipeMaterial: z.enum(VT_PIPE_MATERIALS).optional(),
    // Passage lengths (used by the diameter auto-calc).
    waterAerien: z.boolean().optional(),
    waterAerienLength: z.number().optional(),
    waterEnterre: z.boolean().optional(),
    waterEnterreLength: z.number().optional(),
    waterInterieur: z.boolean().optional(),
    waterInterieurLength: z.number().optional(),
    waterCarrossable: z.boolean().optional(),
    waterCarrossableLength: z.number().optional(),
    waterPression: z.number().optional(),
    waterDiameterJustification: z.string().optional(),
    waterCoudes90: z.number().optional(),
    waterRegardACreer: z.enum(VT_OUI_NON).optional(),
    waterRegardProfondeur: z.number().optional(),
    waterNotes: z.string().optional(),
    waterKitEtancheite: z.enum(VT_OUI_NON).optional(),
    photoCheminementEau: mediaListSchema.optional(),
    photoCompteurEau: mediaListSchema.optional(),

    // Wastewater
    wastewaterConnectionType: z.enum(VT_WATER_CONNECTION_TYPES).optional(),
    wastewaterDiameter: z.enum(VT_WASTEWATER_DIAMETERS).optional(),
    wastewaterDiameterAutreDetail: z.string().optional(),
    wastewaterMaterial: z.enum(VT_WASTEWATER_MATERIALS).optional(),
    wastewaterMaterialAutreDetail: z.string().optional(),
    // Passage lengths + slope calc inputs.
    eauxAerien: z.boolean().optional(),
    eauxAerienLength: z.number().optional(),
    eauxEnterre: z.boolean().optional(),
    eauxEnterreLength: z.number().optional(),
    eauxInterieur: z.boolean().optional(),
    eauxInterieurLength: z.number().optional(),
    eauxDepth: z.number().optional(), // profondeur en cm au point de raccordement
    eauxPenteManual: z.number().optional(), // override % (sinon calculée)
    eauxPompeType: z.string().optional(), // requise si pente < 2%
    wastewaterDiameterJustification: z.string().optional(),
    wastewaterConnectionPoint: z.string().optional(),
    wastewaterCheminementDescription: z.string().optional(),
    photoCheminementEauxUsees: mediaListSchema.optional(),
    wastewaterCarrossable: z.boolean().optional(),
    wastewaterCarrossableLength: z.number().optional(),
    wastewaterNotes: z.string().optional(),
    wastewaterKitEtancheite: z.enum(VT_OUI_NON).optional(),

    // Gas (optional)
    gasPresent: z.enum(VT_OUI_NON).optional(),
    existingGasConnection: z.boolean().optional(),
    existantRaccordeGaz: z.boolean().optional(),
    gasConnectionRequired: z.boolean().optional(),
    gasType: z.string().optional(),
    gasMeterLocation: z.string().optional(),
    gasDescription: z.string().optional(),
    gasNotes: z.string().optional(),

    // Schema & photos
    photoPlanMasse: mediaListSchema.optional(),
    schemaDescription: z.string().optional(),
    annotations: z.string().optional(),
    schemaNotes: z.string().optional(),
    photoPassageGaz: mediaListSchema.optional(),
    photoPassages: mediaListSchema.optional(),
    photoTableauElectrique: mediaListSchema.optional(),
    photoKitPenetration: mediaListSchema.optional(),
    photoRaccordementEauFroide: mediaListSchema.optional(),
    photoRaccordementEauxUsees: mediaListSchema.optional(),
    photoPointRaccordement: mediaListSchema.optional(),

    utilitiesRemarks: z.string().optional(),
  })
  .partial();

export type VtUtilities = z.infer<typeof vtUtilitiesSchema>;
